import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { FeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useLocation } from '@/services/router/compat';
import { createRealtimeBridge } from './bridge';
import { createCentrifugeConnection } from './centrifuge-connection';
import type { ActiveRealtime } from './events';
import { isDesignStudioPath } from './design-studio-route';
import { getTabId, onTabIdChange } from './tab-id';
import { fetchRealtimeToken } from './token-api';
import { useDsNodeChanges } from './use-ds-node-changes';

/** Installs `window.__hydra__.realtime` while `realtime` is set and removes it when it clears. */
function useRealtimeBridge(realtime: ActiveRealtime | null) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (realtime === null) return;
    const bridge = createRealtimeBridge({ ...realtime, queryClient });
    const hydra = (window.__hydra__ ??= {});
    hydra.realtime = bridge;
    return () => {
      if (hydra.realtime === bridge) hydra.realtime = undefined;
    };
  }, [realtime, queryClient]);
}

type RealtimeProviderProps = { children: ReactNode };

/**
 * Opens one Centrifuge connection for the tab, for the current workspace, while the account has
 * the `ds-collaboration-in-services` flag and the route is a Design Studio page. Moving between
 * Design Studio pages keeps the connection; leaving Design Studio closes it. The children render
 * the same either way, so a flag flip or a route change never remounts the app below.
 */
export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const enabled = useFeatureFlag(FeatureFlag.DsCollaborationInServices);
  const { pathname } = useLocation();
  const workspace = useCurrentWorkspace();
  const environmentId = enabled && isDesignStudioPath(pathname) && workspace?.id != null ? workspace.id : null;
  const tab = useSyncExternalStore(onTabIdChange, getTabId);

  // Creating a connection is free; it only dials once started.
  const connection = useMemo(
    () => (environmentId === null ? null : createCentrifugeConnection(() => fetchRealtimeToken(environmentId), tab)),
    [environmentId, tab],
  );
  useEffect(() => {
    connection?.start();
    return () => connection?.stop();
  }, [connection]);

  const realtime = useMemo(
    () => (connection === null || environmentId === null ? null : { transport: connection, environmentId, tab }),
    [connection, environmentId, tab],
  );

  useRealtimeBridge(realtime);
  useDsNodeChanges(realtime);

  return children;
}
